import { useMemo, useCallback } from 'react';
import NsStandout from '@britishgasenergy/react-nucleus/dist/nuc-components/NsStandout';

import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { setConsumption } from 'state/consumption/consumption-slice';
import { NsConsumptionInput } from 'components/common/ns-consumption-input/NsConsumptionInput';
import { NsAlert } from 'components/common/ns-alert/NsAlert';
import { useConsumption } from 'hooks/useConsumption';
import { FUEL_TYPES } from 'config/app-config';

export const warningTestId = 'consumptionActualFormWarningTestId';
export const consumptionActualFormTestId = 'consumptionActualFormTestId';

type ConsumptionActualFormProps = {
  isEnergyUsageExceeds: boolean;
};

export const ConsumptionActualForm = ({
  isEnergyUsageExceeds
}: ConsumptionActualFormProps) => {
  const dispatch = useDispatch();

  const { consumption } = useConsumption();
  const selectedFuelType = useSelector(selectFuelType, shallowEqual);

  const handleChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      const numericValue = parseInt(value, 10);
      const updatedValue = { [name]: numericValue };
      dispatch(setConsumption(updatedValue));
    },
    [dispatch]
  );

  return useMemo(
    () => (
      <div data-testid={consumptionActualFormTestId}>
        {Object.values(FUEL_TYPES)
          .filter((fuel) => !!selectedFuelType[fuel])
          .map((fuel) => (
            <NsConsumptionInput
              key={fuel}
              fuelType={fuel}
              onChange={handleChange}
              value={`${consumption[fuel] || ''}`}
            />
          ))}
        <br />
        {!isEnergyUsageExceeds && (
          <>
            <NsStandout>
              <h5>How to find your annual energy usage</h5>
              <p>
                You can easily find your annual energy usage on your current
                energy supplier&apos;s mobile app, online web account or energy
                bill.
              </p>
            </NsStandout>
          </>
        )}

        {isEnergyUsageExceeds && (
          <>
            <NsAlert
              testId={warningTestId}
              state="warning"
              heading="Your energy use seems high"
            >
              <p>
                Could you double-check your figures to make sure they&apos;re
                right?
              </p>
              <p>If they are, you can keep going with your quote.</p>
            </NsAlert>
          </>
        )}
      </div>
    ),
    [isEnergyUsageExceeds, selectedFuelType, handleChange, consumption]
  );
};
